import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ServicesSection from "./components/ServicesSection";
import HeroSection from "./components/HeroSection";
import TrustedByBar from "./components/TrustedByBar";
import IntroSection from "./components/IntroSection";
import ChallengesSection from "./components/ChallengesSection";
import WealthMachineSection from "./components/WealthMachineSection";
import UseCasesSection from "./components/UseCasesSection";
import AIBrainSection from "./components/AIBrainSection";
import WhoIsItForSection from "./components/WhoIsItForSection";
import CaseStudiesSection from "./components/CaseStudiesSection";
import WhyChooseUsSection from "./components/WhyChooseUsSection";
import ContactSection from "./components/ContactSection";
import RecentListingsSection from './components/RecentListingsSection';
import { publicApi } from "@/services/publicApi";
import { mapListingToCard } from "@/services/listingMapper";
import type { ListingCard } from "@/services/listingMapper";
import { useApiData } from "@/hooks/useApiData";
import { usePageContent } from "@/hooks/usePageContent";
import { usePageMeta } from "@/hooks/usePageMeta";

const exploreLinks = [
  { label: "Listings", href: "/listings", icon: "ri-landscape-line", desc: "Browse verified land parcels" },
  { label: "Services", href: "/services", icon: "ri-building-2-line", desc: "10 ways to monetize your land" },
  { label: "Strategies", href: "/strategies", icon: "ri-bar-chart-line", desc: "Compare monetization strategies" },
  { label: "AI Brain", href: "/ai-technology", icon: "ri-cpu-line", desc: "How our AI analyzes your land" },
  { label: "Pricing", href: "/pricing", icon: "ri-money-rupee-circle-line", desc: "Transparent pricing model" },
  { label: "Work Flow", href: "/how-it-works", icon: "ri-flow-chart", desc: "6-step process from start to income" },
  { label: "FAQ", href: "/faq", icon: "ri-question-answer-line", desc: "Answers to common questions" },
  { label: "Partners", href: "/partners", icon: "ri-team-line", desc: "200+ brands & developers" },
];

function FeaturedListingsSection() {
  const { getContent } = usePageContent('homepage');
  const heading = getContent('home-featured-listings-heading') || 'Featured Land Listings';
  const subheading = getContent('home-featured-listings-sub') || 'Real land parcels listed by owners, matched by AI. Browse and enquire directly.';

  const { data: apiResponse, loading, error } = useApiData(
    () => publicApi.getListings({ status: "available", per_page: 3, page: 1 }),
    []
  );

  const listings: ListingCard[] = apiResponse?.data
    ? apiResponse.data.map(mapListingToCard)
    : [];

  return (
    <section className="py-24 bg-background-100">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Live Marketplace
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-2">
              {heading}
            </h2>
            <p className="text-foreground-600 max-w-lg">
              {subheading}
            </p>
          </div>
          <Link
            to="/listings"
            className="inline-flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap shrink-0"
          >
            View All Listings
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl card-dark overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-background-200/50" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-background-200/50 rounded w-1/2" />
                  <div className="h-5 bg-background-200/50 rounded w-3/4" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-10 bg-background-200/50 rounded-lg" />
                    <div className="h-10 bg-background-200/50 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-foreground-500 text-sm">Featured listings unavailable right now.</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mx-auto mb-4">
              <i className="ri-inbox-line text-xl text-primary-500" />
            </div>
            <p className="text-foreground-500 text-sm">No listings available yet. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-5">
              {listings.map((listing) => (
                <Link
                  key={listing.id}
                  to="/listings"
                  className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={listing.images[0] || ""}
                      alt={listing.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${listing.statusColor}`}>
                        {listing.status}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                        {listing.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                      <i className="ri-map-pin-line text-primary-500" />
                      {listing.location}
                    </div>
                    <h3 className="font-display font-semibold text-base text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {listing.title}
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mt-auto">
                      <div className="p-2.5 rounded-lg bg-background-200/50 border border-primary-800/20">
                        <div className="text-xs text-foreground-500">Size</div>
                        <div className="text-sm font-semibold text-foreground-950">{listing.sizeDisplay}</div>
                      </div>
                      <div className="p-2.5 rounded-lg bg-background-200/50 border border-primary-800/20">
                        <div className="text-xs text-foreground-500">Expected Rent</div>
                        <div className="text-sm font-semibold text-primary-600">{listing.expectedRentDisplay}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-12">
              <div className="p-6 rounded-2xl card-dark group hover:border-primary-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mx-auto mb-4 group-hover:border-primary-500/60 transition-colors">
                  <i className="ri-user-star-line text-xl text-primary-500" />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">Own Land? List It Free</h3>
                <p className="text-sm text-foreground-600 mb-5">List your land at zero cost. Pay only when a deal closes. We handle everything — photos, marketing, legal docs.</p>
                <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
                  List Your Land <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="p-6 rounded-2xl card-dark group hover:border-primary-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-500/10 mx-auto mb-4 group-hover:bg-secondary-500/20 transition-colors">
                  <i className="ri-building-2-line text-xl text-secondary-500" />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">Need Land? Browse Now</h3>
                <p className="text-sm text-foreground-600 mb-5">450+ AI-vetted parcels. Tell us your requirements and we&apos;ll match you with verified land — fast and transparent.</p>
                <Link to="/listings" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-secondary-500/40 text-primary-600 font-medium text-sm hover:bg-secondary-500/10 transition-colors whitespace-nowrap">
                  Browse Listings <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function HomePage() {
  usePageMeta({
    title: "CashMyProperty.ai — AI-Powered Land Monetization Platform for India",
    description:
      "Turn idle land into a wealth machine. CashMyProperty.ai uses AI to find the Highest & Best Use of your property — JDA, leasing, franchise & land-as-equity strategies without selling. India's #1 land monetization platform.",
    canonicalUrl: "https://cashmyproperty.ai/",
  });
  const { getContent } = usePageContent('homepage');
  const featuredHeading = getContent('home-featured-listings-heading') || 'Featured Land Listings';
  const exploreHeading = getContent('home-explore-heading') || 'Discover More';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <HeroSection />
        <TrustedByBar />
        <IntroSection />
        <RecentListingsSection />
        <ServicesSection />
        <ChallengesSection />
        <WealthMachineSection />
        <UseCasesSection />
        <WhyChooseUsSection />
        <AIBrainSection />
        <WhoIsItForSection />
        <CaseStudiesSection />
        <FeaturedListingsSection />

        <section className="py-24 bg-background-100">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Explore</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">{exploreHeading}</h2>
              <p className="text-foreground-600">Dive deeper into each aspect of our platform and find the right path for your land.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {exploreLinks.map((link) => (
                <Link key={link.label} to={link.href} className="group p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4 group-hover:border-primary-500/60 transition-colors">
                    <i className={`${link.icon} text-2xl text-primary-500`} />
                  </div>
                  <h3 className="font-display font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors">{link.label}</h3>
                  <p className="text-sm text-foreground-600">{link.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm text-primary-600 font-medium">
                    Learn more <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}